// Vehicles and drones at the table (issue 1533; docs/MRCS/shadowrun-3.md §9 step 19; the book p. 43, 130-149): where the
// table's system declares them (`features.combat.vehicles`), two things a rigger does, each the system's data:
//
// 1. Bringing a vehicle out (`vehicle.place`): a vehicle is an entry of the system's vehicle family, an item that is also
//    a creature. The server reads it again at its address and puts its token beside the rigger's, the rigger's owners',
//    as the table's DM act, its numbers the entry's fields (`vehicleNumbers`) and its condition monitors the data's —
//    so damage goes down the vehicle's own monitor, as any token's does.
// 2. A vehicle test (`vehicle.test`, a Driving or Maneuver test): the driver's vehicle skill — or, for a drone acting on
//    its own, its Pilot — with the autonav's dice where the test takes them, and pool dice from the system's vehicle
//    pool, against the vehicle's Handling (on the road or off it), raised by the terrain, the autonav where the test
//    counts it against the driver, the DM's typed situations, the vehicle's damage and the driver's wounds, and lowered
//    by the rig's level (times the test's `rig`) for a rigger jacked in. Each success counts; one is enough.
//
// The record is a success test's (`TestFacts.vehicle`, rules/test.ts): its one side is the driver's (or the drone's), so
// the log, privacy and projection are a test's own. Nothing here throws a die on its own: the server hands the
// generator in, a test a scripted list.
import { MAX_POOL, poolDisaster, poolText, testDice } from '../dice/pool.js';
import { parseTestDice } from './system.js';
import { testDiceData } from './test.js';
import { successesOf, throwOne } from './staged.js';
export const VEHICLE_LIMITS = { name: 80, rating: MAX_POOL, stat: 100, modifiers: 4, modifier: 50, label: 40, rig: 6, fields: 16 };
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
const ADDRESS_RE = /^[a-z][a-z0-9_]{0,62}\/[a-z0-9][a-z0-9-]{0,127}$/;
// eslint-disable-next-line no-control-regex
const LINE_BAD = /[\u0000-\u001f\u007f]/;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isInt = (v, min, max) => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
const isText = (v, max) => typeof v === 'string' && v.trim().length >= 1 && v.length <= max && !LINE_BAD.test(v);
const only = (v, keys) => Object.keys(v).every((k) => keys.includes(k));
const keyLabel = (v) => (isObj(v) && only(v, ['key', 'label']) && typeof v.key === 'string' && KEY_RE.test(v.key) && isText(v.label, 40) ? { key: v.key, label: v.label.trim() } : null);
function parseTerms(v, most) {
    if (v === undefined)
        return [];
    if (!Array.isArray(v) || v.length > most)
        return null;
    const out = [];
    for (const x of v) {
        if (!isObj(x) || !only(x, ['label', 'value']) || !isText(x.label, VEHICLE_LIMITS.label) || !isInt(x.value, -VEHICLE_LIMITS.modifier, VEHICLE_LIMITS.modifier))
            return null;
        out.push({ label: x.label.trim(), value: x.value });
    }
    return out;
}
function parseSkill(s) {
    if (!isObj(s) || !only(s, ['name', 'rating', 'ref']) || !isText(s.name, VEHICLE_LIMITS.name) || !isInt(s.rating, 1, VEHICLE_LIMITS.rating) || (s.ref !== undefined && (typeof s.ref !== 'string' || !ADDRESS_RE.test(s.ref))))
        return null;
    return { name: s.name.trim(), rating: s.rating, ...(typeof s.ref === 'string' ? { ref: s.ref } : {}) };
}
/** A vehicle off the wire or its entry: exact shape or null. Its numbers are whole and short. */
export function parseVehicleEntry(v) {
    if (!isObj(v) || !only(v, ['name', 'ref', 'stats']) || !isText(v.name, VEHICLE_LIMITS.name) || (v.ref !== undefined && (typeof v.ref !== 'string' || !ADDRESS_RE.test(v.ref))))
        return null;
    if (!isObj(v.stats) || Object.keys(v.stats).length > VEHICLE_LIMITS.fields)
        return null;
    const stats = {};
    for (const [k, n] of Object.entries(v.stats)) {
        if (!KEY_RE.test(k) || !isInt(n, 0, VEHICLE_LIMITS.stat))
            return null;
        stats[k] = n;
    }
    return { name: v.name.trim(), ...(typeof v.ref === 'string' ? { ref: v.ref } : {}), stats };
}
/** A request to bring a vehicle out, from the wire: exact shape or null. Untrusted input, always. */
export function parseVehiclePlaceRequest(v) {
    if (!isObj(v) || v.kind !== 'vehicle.place' || !only(v, ['kind', 'rigger', 'vehicle', 'called', 'private']))
        return null;
    const vehicle = parseVehicleEntry(v.vehicle);
    if (!UUID_RE.test(String(v.rigger)) || !vehicle || (v.called !== undefined && !isText(v.called, VEHICLE_LIMITS.name)) || (v.private !== undefined && typeof v.private !== 'boolean'))
        return null;
    return { kind: 'vehicle.place', rigger: String(v.rigger), vehicle, ...(typeof v.called === 'string' ? { called: v.called.trim() } : {}), private: v.private === true };
}
/** A vehicle test request from the wire: exact shape or null. Untrusted input, always. */
export function parseVehicleTestRequest(v) {
    if (!isObj(v) || v.kind !== 'vehicle.test')
        return null;
    if (!only(v, ['kind', 'vehicle', 'driver', 'test', 'skill', 'rigged', 'rig', 'pool', 'autonav', 'offroad', 'terrain', 'modifiers', 'private']))
        return null;
    if (!UUID_RE.test(String(v.vehicle)) || (v.driver !== undefined && !UUID_RE.test(String(v.driver))) || typeof v.test !== 'string' || !KEY_RE.test(v.test))
        return null;
    // A driver drives with a skill; a vehicle on its own has none.
    const skill = v.skill === undefined ? undefined : parseSkill(v.skill);
    if (skill === null || (v.driver === undefined) !== (skill === undefined))
        return null;
    let pool;
    if (v.pool !== undefined) {
        const p = v.pool;
        if (!skill || !isObj(p) || !only(p, ['key', 'dice']) || typeof p.key !== 'string' || !KEY_RE.test(p.key) || !isInt(p.dice, 1, VEHICLE_LIMITS.rating))
            return null;
        pool = { key: p.key, dice: p.dice };
    }
    const modifiers = parseTerms(v.modifiers, VEHICLE_LIMITS.modifiers);
    if (!modifiers || (v.rig !== undefined && !isInt(v.rig, 1, VEHICLE_LIMITS.rig)) || (v.terrain !== undefined && (typeof v.terrain !== 'string' || !KEY_RE.test(v.terrain))))
        return null;
    for (const k of ['rigged', 'autonav', 'offroad', 'private'])
        if (v[k] !== undefined && typeof v[k] !== 'boolean')
            return null;
    return {
        kind: 'vehicle.test', vehicle: String(v.vehicle), ...(typeof v.driver === 'string' ? { driver: v.driver } : {}), test: v.test, ...(skill ? { skill } : {}),
        rigged: v.rigged === true, ...(typeof v.rig === 'number' ? { rig: v.rig } : {}), ...(pool ? { pool } : {}),
        autonav: v.autonav === true, offroad: v.offroad === true, ...(typeof v.terrain === 'string' ? { terrain: v.terrain } : {}), modifiers, private: v.private === true,
    };
}
export const NO_VEHICLE_RULES = "This table's game system names no vehicles, so the table cannot drive one.";
/** What a test of a token that is no vehicle is told. */
export const NOT_A_VEHICLE = 'That token carries no vehicle\'s numbers: bring the vehicle out from its entry.';
/**
 * A vehicle's numbers from its entry (§9 step 19): each of the data's fields the entry holds, as a sheet number of the
 * data's purpose named by the field's words; each monitor's maximum, a number or one of those fields (at least 1). A
 * string is a refusal in words.
 */
export function vehicleNumbers(rules, stats) {
    if (stats[rules.handling] === undefined)
        return 'That vehicle has no Handling the table reads.';
    const sheet = [];
    for (const f of rules.fields) {
        const v = stats[f];
        if (v !== undefined)
            sheet.push({ purpose: rules.purpose, words: f.split('_'), value: v });
    }
    const resources = {};
    for (const [key, m] of Object.entries(rules.monitors)) {
        const max = typeof m === 'number' ? m : Math.max(1, stats[m] ?? 0);
        resources[key] = { current: max, max };
    }
    return { sheet, resources };
}
/** A vehicle's field off its token's sheet numbers: the number whose words are exactly the field's, or null. */
export function vehicleStat(sheet, purpose, field) {
    if (!field)
        return null;
    const words = field.split('_');
    const n = sheet?.find((x) => x.purpose === purpose && x.words.length === words.length && words.every((w) => x.words.includes(w)));
    return n && isInt(n.value, 0, VEHICLE_LIMITS.stat) ? n.value : null;
}
const clamp = (n) => Math.max(-VEHICLE_LIMITS.modifier, Math.min(VEHICLE_LIMITS.modifier, n));
const RIG = 'control rig';
const AUTONAV = 'autonav';
const DAMAGED = 'vehicle damaged';
const WOUNDED = 'wounded';
const PILOT = 'Pilot';
/** The test's TN terms in the book's order: terrain, situations, autonav, the rig, the vehicle's damage, the driver's wounds. */
function termsOf(t, req, r, autonav, rig, m) {
    const out = [];
    if (req.terrain !== undefined) {
        const terrain = r.terrains.find((x) => x.key === req.terrain);
        if (!terrain)
            return 'That is not one of this game system\'s terrains.';
        const add = t.terrain[terrain.key] ?? 0;
        if (add)
            out.push({ label: `${terrain.label.toLowerCase()} terrain`.slice(0, VEHICLE_LIMITS.label), value: add });
    }
    out.push(...req.modifiers);
    if (req.autonav && t.autonav === 'target' && autonav !== null && autonav + t.autonavAdd)
        out.push({ label: AUTONAV, value: autonav + t.autonavAdd });
    if (rig !== null && t.rig)
        out.push({ label: RIG, value: clamp(-rig * t.rig) });
    if (m.damage)
        out.push({ label: DAMAGED, value: clamp(m.damage) });
    if (m.wound)
        out.push({ label: WOUNDED, value: clamp(m.wound) });
    return out;
}
/** A vehicle test made concrete: thrown with the generator by the system's rules. A string is a refusal in words. */
export function makeVehicleTest(req, m, rng) {
    const r = m.rules;
    const t = r.tests.find((x) => x.key === req.test);
    if (!t)
        return 'That is not one of this game system\'s vehicle tests.';
    const road = vehicleStat(m.vehicle, r.purpose, r.handling);
    if (road === null)
        return NOT_A_VEHICLE;
    const off = vehicleStat(m.vehicle, r.purpose, r.offroad);
    if (req.offroad && off === null)
        return 'That vehicle has one Handling, on any ground.';
    const handling = req.offroad ? off : road;
    const autonav = vehicleStat(m.vehicle, r.purpose, r.autonav);
    if (req.autonav && (autonav === null || t.autonav === null))
        return autonav === null ? 'That vehicle has no autonav.' : `The autonav does not help a ${t.label}.`;
    // Who drives: the driver's skill, or the vehicle's own Pilot.
    let skill = null;
    let rating;
    if (req.skill) {
        skill = { ...req.skill };
        rating = req.skill.rating;
    }
    else {
        const pilot = vehicleStat(m.vehicle, r.purpose, r.pilot);
        if (pilot === null || pilot < 1)
            return 'That vehicle has no Pilot to act on its own: name its driver.';
        rating = pilot;
    }
    // A rigger jacked in takes the rig's level off; only one may draw on the pool.
    let rig = null;
    if (req.rigged) {
        if (!req.skill)
            return 'A vehicle on its own is not rigged.';
        if (m.rig === null)
            return `The driver's ${r.rig.label} could not be read from its sheet: type its level.`;
        rig = m.rig;
    }
    if (m.spent && rig === null)
        return `Only a rigger jacked in draws on the ${m.spent.label}.`;
    const autoDice = req.autonav && t.autonav === 'dice' ? autonav : 0;
    const terms = termsOf(t, req, r, autonav, rig, m);
    if (typeof terms === 'string')
        return terms;
    const base = rating + autoDice;
    const spent = m.spent ? { key: m.spent.key, label: m.spent.label, dice: m.spent.dice } : null;
    const dice = base + (spent?.dice ?? 0);
    if (dice > MAX_POOL)
        return 'Those dice cannot be thrown.';
    const thrown = throwOne(r.dice, { label: skill?.name ?? PILOT, rating: base, spent, dice, target: handling, base: 'Handling', terms }, rng);
    if (typeof thrown === 'string')
        return thrown;
    const side = {
        name: m.who.name, ...(m.who.token ? { token: m.who.token } : {}),
        rated: { kind: skill ? 'skill' : 'typed', name: skill?.name ?? PILOT, rating: base, ...(skill?.ref ? { ref: skill.ref } : {}) },
        dice, ...(m.spent ? { spent: { ...m.spent } } : {}), target: handling, terms, tn: thrown.tn, pool: thrown.pool,
    };
    return {
        open: false, sides: [side], rules: { dice: testDiceData(r.dice), open: null, ties: null },
        vehicle: {
            test: { key: t.key, label: t.label }, vehicle: { ...m.vehicleWho }, handling: { value: handling, offroad: req.offroad },
            skill, autonav: autoDice, rig: rig === null ? null : { level: rig, times: t.rig },
        },
    };
}
/** The test's outcome: its successes (none on a disaster); one is enough. */
export function vehicleOutcome(f) {
    const s = f.sides[0];
    const successes = successesOf(f.rules.dice, { dice: s.dice, tn: s.tn ?? 1, pool: s.pool });
    const dd = parseTestDice(f.rules.dice);
    const concrete = dd && s.dice ? testDice(dd, { dice: s.dice, target: s.tn ?? 1 }) : null;
    const disaster = concrete ? poolDisaster(concrete, s.pool) : false;
    return { successes, disaster, succeeded: !disaster && successes >= 1 };
}
// ── Stored records ────────────────────────────────────────────────────────
/** Stored vehicle facts beside a test's: exact shape or null. The side is thrown against the Handling, its rating the skill (or Pilot) and the autonav's dice. */
export function parseVehicleFacts(v, side) {
    if (!isObj(v) || !only(v, ['test', 'vehicle', 'handling', 'skill', 'autonav', 'rig']) || !side)
        return null;
    const test = keyLabel(v.test);
    const w = v.vehicle;
    if (!test || !isObj(w) || !only(w, ['name', 'token']) || !(w.name === null || isText(w.name, VEHICLE_LIMITS.name)) || (w.token !== undefined && !UUID_RE.test(String(w.token))))
        return null;
    const h = v.handling;
    if (!isObj(h) || !only(h, ['value', 'offroad']) || !isInt(h.value, 0, VEHICLE_LIMITS.stat) || typeof h.offroad !== 'boolean')
        return null;
    const skill = v.skill === null ? null : parseSkill(v.skill) ?? undefined;
    if (skill === undefined)
        return null;
    if (!isInt(v.autonav, 0, VEHICLE_LIMITS.stat))
        return null;
    let rig = null;
    if (v.rig !== null) {
        const g = v.rig;
        if (!isObj(g) || !only(g, ['level', 'times']) || !isInt(g.level, 1, VEHICLE_LIMITS.rig) || !isInt(g.times, 0, 4))
            return null;
        rig = { level: g.level, times: g.times };
    }
    // The side: the skill (or Pilot) and the autonav's dice, against the Handling; pool dice only with a rig.
    if (side.target !== h.value || side.defaulted || (side.spent && !rig) || (skill && side.rated.rating !== skill.rating + v.autonav) || (!skill && (side.rated.kind !== 'typed' || side.spent)))
        return null;
    return {
        test, vehicle: { name: w.name, ...(typeof w.token === 'string' ? { token: w.token } : {}) }, handling: { value: h.value, offroad: h.offroad },
        skill, autonav: v.autonav, rig,
    };
}
// ── The lines ─────────────────────────────────────────────────────────────
const plural = (n) => `${n} success${n === 1 ? '' : 'es'}`;
const signed = (n) => (n < 0 ? `− ${-n}` : `+ ${n}`);
/**
 * The test's line: "Rigger X, Mach 6, Accelerating or Braking Test: Car 5 + 4 Control Pool = 9 dice vs TN 3 (3 Handling
 * + 2 Maneuver Score − 4 control rig): … → 2 successes".
 */
export function vehicleLine(f) {
    const s = f.sides[0];
    const v = f.vehicle;
    const who = [s.name, v.vehicle.name && v.vehicle.name !== s.name ? v.vehicle.name : null].filter(Boolean).join(', ');
    const head = `${who ? `${who}, ` : ''}${v.test.label}`;
    const skillN = v.skill ? v.skill.rating : s.rated.rating;
    const parts = [`${s.rated.name} ${skillN}`, ...(v.autonav ? [`${v.autonav} Autonav`] : []), ...(s.spent ? [`${s.spent.dice} ${s.spent.label}`] : [])];
    const dice = parts.length > 1 ? `${parts.join(' + ')} = ${s.dice} dice` : parts[0];
    const asked = s.target + s.terms.reduce((n, t) => n + t.value, 0);
    const raised = asked !== s.tn ? `, at least ${s.tn}` : '';
    const handling = `${s.target} ${v.handling.offroad ? 'off-road Handling' : 'Handling'}`;
    const tn = `TN ${s.tn} (${handling}${s.terms.map((t) => ` ${signed(t.value)} ${t.label}`).join('')}${raised})`;
    const o = vehicleOutcome(f);
    return `${head}: ${dice} vs ${tn}: ${poolText(s.pool)} → ${o.disaster ? 'disaster' : plural(o.successes)}`;
}
/** The outcome's line: "The Driving Test succeeds: 2 successes", "Disaster: the Ramming Test fails", "No successes: …". */
export function vehicleOutcomeLine(f) {
    const o = vehicleOutcome(f);
    const what = `the ${f.vehicle.test.label}`;
    if (o.disaster)
        return `Disaster: ${what} fails`;
    if (!o.succeeded)
        return `No successes: ${what} fails`;
    return `The ${f.vehicle.test.label} succeeds: ${plural(o.successes)}`;
}
/** Every line of a vehicle test. */
export const vehicleLines = (f) => [vehicleLine(f), vehicleOutcomeLine(f)];
/** Whether a test's record is a vehicle test. */
export const isVehicleTest = (f) => f.vehicle !== undefined;
//# sourceMappingURL=vehicle.js.map